/**
 * PdfEmbed — inline PDF preview for the evidence drawer.
 *
 * Fetches a short-lived presigned-GET URL from the backend, then embeds
 * the PDF in an iframe. Uses the standard PDF URL fragment syntax
 * (`#page=N&zoom=100`) which is supported by Chrome, Edge, Firefox,
 * and Safari's built-in PDF viewers.
 *
 * Why iframe and not pdf.js / react-pdf?
 *   - Zero new dependencies. The browser already ships a PDF viewer.
 *   - 5KB component vs ~2MB pdf.js bundle.
 *   - No CORS or worker setup complexity.
 *   - Native print, search, download — features we'd reimplement otherwise.
 *
 * Trade-off: less control over rendering than pdf.js. If we need
 * bounding-box annotations later, we'd switch then.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Download, FileText, AlertTriangle, Loader2 } from 'lucide-react';
import { ingestionApi } from '@/api/ingestion-api';

export interface PdfEmbedProps {
  loanFileId: string;
  documentId: string;
  fileName: string;
  /** Optional page number to jump to on load (1-indexed). */
  initialPage?: number | null;
  /** Iframe height. Defaults to 480px — fits well inside the drawer. */
  heightPx?: number;
}

export function PdfEmbed({
  loanFileId,
  documentId,
  fileName,
  initialPage,
  heightPx = 480,
}: PdfEmbedProps) {
  // Cache key includes documentId so different docs in the same drawer
  // session each get their own short-lived URL.
  const urlQuery = useQuery({
    queryKey: ['documentUrl', loanFileId, documentId],
    queryFn: () => ingestionApi.getDocumentUrl(loanFileId, documentId),
    // The presigned URL is valid for 15 min. We cache for 10 min to be
    // safe against clock skew + render time before expiry.
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
  });

  const [iframeError, setIframeError] = useState(false);

  if (urlQuery.isLoading) {
    return (
      <div
        className="rounded-md border border-glass-hairline bg-slate-50/40 flex items-center justify-center"
        style={{ height: heightPx }}
      >
        <div className="flex items-center gap-2 text-text-tertiary text-caption">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading PDF…
        </div>
      </div>
    );
  }

  if (urlQuery.isError || !urlQuery.data) {
    const msg =
      urlQuery.error instanceof Error
        ? urlQuery.error.message
        : 'Failed to load PDF';
    return (
      <div className="rounded-md border border-red-200 bg-red-50/40 p-3">
        <div className="flex items-center gap-2 text-red-800 text-caption font-medium mb-1">
          <AlertTriangle className="w-3.5 h-3.5" />
          Could not load PDF
        </div>
        <div className="text-caption text-red-700">{msg}</div>
        <button
          type="button"
          onClick={() => urlQuery.refetch()}
          className="mt-2 text-caption text-red-800 underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const { url, originalFileName, mimeType, pageCount } = urlQuery.data;
  // Add the page/zoom fragment to the URL for jump-to-page. The browser's
  // built-in PDF viewer reads this and starts at that page.
  const pageFragment = initialPage && initialPage > 0
    ? `#page=${initialPage}&zoom=page-width`
    : '#zoom=page-width';
  const iframeSrc = `${url}${pageFragment}`;
  const isPdf = mimeType === 'application/pdf' || originalFileName.toLowerCase().endsWith('.pdf');

  return (
    <div className="rounded-md border border-glass-hairline overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-2.5 py-1.5 bg-slate-50 border-b border-glass-hairline">
        <FileText className="w-3.5 h-3.5 text-text-tertiary shrink-0" />
        <div className="text-caption font-medium text-text-primary truncate flex-1 min-w-0">
          {fileName || originalFileName}
        </div>
        {pageCount && (
          <span className="text-micro text-text-tertiary shrink-0">
            {pageCount} pages
          </span>
        )}
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-micro text-brand-blue hover:bg-brand-blue/[0.06] px-1.5 py-0.5 rounded transition-colors shrink-0"
          title="Open PDF in a new tab"
        >
          <ExternalLink className="w-3 h-3" />
          Open
        </a>
        <a
          href={url}
          download={originalFileName}
          className="inline-flex items-center gap-1 text-micro text-brand-blue hover:bg-brand-blue/[0.06] px-1.5 py-0.5 rounded transition-colors shrink-0"
          title="Download PDF"
        >
          <Download className="w-3 h-3" />
          Download
        </a>
      </div>

      {/* Embed */}
      {!isPdf ? (
        <div
          className="flex items-center justify-center bg-slate-50 text-caption text-text-secondary"
          style={{ height: heightPx }}
        >
          <div className="text-center px-4">
            <FileText className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
            <div className="font-medium">Not a PDF — preview unavailable</div>
            <div className="text-micro text-text-tertiary mt-1">
              MIME type: {mimeType}
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-blue text-caption mt-2 inline-block underline"
            >
              Open in new tab
            </a>
          </div>
        </div>
      ) : iframeError ? (
        <div
          className="flex items-center justify-center bg-slate-50 text-caption text-text-secondary p-4"
          style={{ height: heightPx }}
        >
          <div className="text-center">
            <AlertTriangle className="w-6 h-6 text-amber-700 mx-auto mb-2" />
            <div className="font-medium">Inline preview blocked</div>
            <div className="text-micro text-text-tertiary mt-1 mb-3">
              Your browser blocked the embedded preview.
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-caption bg-brand-blue text-white px-3 py-1.5 rounded-md"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open in new tab
            </a>
          </div>
        </div>
      ) : (
        <iframe
          src={iframeSrc}
          title={`PDF preview: ${originalFileName}`}
          className="w-full block bg-white"
          style={{ height: heightPx, border: 0 }}
          onError={() => setIframeError(true)}
        />
      )}
    </div>
  );
}
